import React, { useMemo } from 'react';
import classNames from 'classnames';
import Img from 'assets/images/rocket-launch.webp';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from 'ncoded-component-library';
import CopyTextBanner from 'components/CopyTextBanner';
import ResolveImage from 'components/ResolveImage';
import DocumentsRfiSecurity from '../DocumentsRfiSecurity';
import AutomaticallyAddFilesToDr from '../AutomaticallyAddFilesToDr';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { getResolveUrl, getRFILinkRoute } from 'utils';
import useFileSecurityInitialValues from 'router/subrouters/DataRoom/hooks/useFileSecurityInitialValues';

import './RfiCreatedSuccessScreen.styles.scss';

type RfiCreatedSuccessScreenProps = {
  className?: string;
  rfi: DataRoomRFI;
  onCreateNew: () => void;
};

const RfiCreatedSuccessScreen: React.FC<RfiCreatedSuccessScreenProps> = (
  props,
) => {
  const { className, rfi, onCreateNew } = props;

  const {
    _id,
    externalKey,
    grantAccessDataRoomMemberIds,
    automaticFileUploadToDataRoom,
    downloadSecurePdf,
    hasExpiration,
    expirationDays,
    includeWatermark,
    watermarkDiagonal,
    watermarkHeader,
    watermarkFooter,
    watermarkFullName,
    watermarkEmail,
    watermarkIpAddress,
    dataRoomRFICategories,
  } = rfi || {};

  const fileSettingsInitialValues = useFileSecurityInitialValues()({
    downloadSecurePdf,
    hasExpiration,
    expirationDays,
    includeWatermark,
    watermarkDiagonal,
    watermarkHeader,
    watermarkFooter,
    watermarkFullName,
    watermarkEmail,
    watermarkIpAddress,
  });

  const { t } = useTranslation();

  const classes = classNames('rfi-created-success-screen', className);

  const link = useMemo(
    () => getResolveUrl(getRFILinkRoute(externalKey)),
    [externalKey],
  );

  return (
    <div className={classes}>
      <ResolveImage asIllustration src={Img} alt="Success" />
      <h4>{t('fileRequestCreated')}</h4>

      <AutomaticallyAddFilesToDr
        dataRoomRfiId={_id}
        dataRoomRFICategories={dataRoomRFICategories}
        grantAccessDataRoomMemberIds={grantAccessDataRoomMemberIds}
        automaticFileUploadToDataRoom={automaticFileUploadToDataRoom}
      />

      <DocumentsRfiSecurity
        dataRoomRfiId={_id}
        fileSettingsInitialValues={fileSettingsInitialValues}
      />

      <CopyTextBanner text={link} label={t('shareRfiLinkWithRecipients')} />

      <div className="actions">
        <Button onClick={onCreateNew}>{t('createAnotherRequest')}</Button>
        <Link to="../list" className="ncoded-button ncoded-button--outline">
          {t('showAll')}
        </Link>
      </div>
    </div>
  );
};

export default RfiCreatedSuccessScreen;
